import React, { useDeferredValue, useEffect, useMemo, useRef, useState } from "react";

import { useCreatePrintJobMutation, useRecordPrintAttemptMutation, useResolveQrLinksMutation } from "../../../api/printingApi";
import { buildAlignmentTestDocument } from "../../../components/Print/engine/alignmentTestDocument";
import { buildLabelSheetDocument, LABEL_SHEET_MODES } from "../../../components/Print/engine/labelSheetDocument";
import { LABEL_TEMPLATE_IDS, resolveLabelTemplate } from "../../../components/Print/engine/labelTemplates";
import { openPrintDocument } from "../../../components/Print/engine/printDocument";
import {
  clampStartPosition,
  computeSheetMetrics,
  getSheetGeometrySignature,
  planSheetLayout,
  validateSheetGeometry,
} from "../../../components/Print/engine/sheetLayout";
import {
  lastSheetUsedOtherGeometry,
  readSheetSuggestion,
  rememberSheetEnd,
} from "../../../components/Print/engine/sheetPositionMemory";
import { Btn, Icon, Notice, Pill, Select } from "../../catalogProductStudio/edit/StudioUi";
import SheetPreview from "../preview/SheetPreview";
import SheetStartControls from "../preview/SheetStartControls";
import { buildAttemptLayout, buildPrintCenterLabelJob, presetContent, presetGeometry } from "../preview/printCenterLabels";
import { readDeviceCalibration, saveDeviceCalibration } from "../presets/deviceCalibration";
import { readTemplateChoice, saveTemplateChoice, templateFromJob } from "../presets/templateChoice";
import { LARGE_JOB_LABELS } from "../state/printSelectionModel";
import { describeVariant, fill, getApiErrorDetails, newIdempotencyKey, plural } from "../printCenterModel";

const PREVIEW_PAGE_LIMIT = 4;
const DENSITY_TONES = { healthy: "success", warning: "warning", unsafe: "danger" };
const OFFSET_STEP = 0.1;

/**
 * mode.type === "new":     { lines: [{ item, quantity, newStock }], source, reprintOfJobId, jobLines }
 * mode.type === "attempt": { job, lines: [{ item, quantity, lineId }], lineIds } — print an existing job again
 */
function PrintReviewDialog({
  tt,
  mode,
  presets,
  presetId,
  onChangePreset,
  isPresetAdmin,
  onOpenPresetSettings,
  onClose,
  onPrinted,
  onResolveConflict,
  onOpenJob,
  onRemoveKeys,
}) {
  const preset = presets.find((entry) => entry.id === presetId) || presets[0];
  const geometry = presetGeometry(preset);
  const appearance = presetContent(preset);
  const metrics = computeSheetMetrics(geometry);
  const slotsPerPage = metrics.slotsPerPage;
  const signature = getSheetGeometrySignature(geometry);

  const [offsets, setOffsets] = useState(() => readDeviceCalibration(preset?.id));
  const [startPosition, setStartPosition] = useState(1);
  // One design for the whole job. A reprint starts from the design it was printed with.
  const [templateId, setTemplateId] = useState(() => (mode.type === "attempt" && templateFromJob(mode.job)) || readTemplateChoice());
  const template = resolveLabelTemplate(templateId);
  const [error, setError] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);
  // Unsafe barcode density needs an explicit "I test-scanned a label" before printing.
  const [densityOverride, setDensityOverride] = useState(false);
  const dialogRef = useRef(null);
  const errorRef = useRef(null);
  // A refusal (e.g. someone else is printing these labels) must not hide below the fold.
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [error]);

  // Keyboard: focus moves into the dialog, Escape closes it (not while
  // printing), and focus returns to where it was.
  useEffect(() => {
    const previouslyFocused = document.activeElement;
    dialogRef.current?.focus();
    return () => previouslyFocused?.focus?.();
  }, []);
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !isPrinting) onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isPrinting, onClose]);
  const idempotencyKeyRef = useRef(newIdempotencyKey("print"));
  const [createJob] = useCreatePrintJobMutation();
  const [recordAttempt] = useRecordPrintAttemptMutation();
  const [resolveQrLinks] = useResolveQrLinksMutation();

  // Designs that draw a QR need each variant's stable link before the labels are
  // built, so preview and print carry the same code.
  const qrVariantIds = useMemo(
    () => [...new Set(mode.lines.map((line) => line.item.variantId).filter(Boolean))].sort((a, b) => a - b),
    [mode.lines]
  );
  const qrVariantKey = qrVariantIds.join(",");
  const [qr, setQr] = useState({ status: "idle", links: {} });
  useEffect(() => {
    if (!template.usesQr || !qrVariantIds.length) {
      setQr({ status: "idle", links: {} });
      return undefined;
    }
    let cancelled = false;
    setQr({ status: "loading", links: {} });
    resolveQrLinks(qrVariantIds)
      .unwrap()
      .then((result) => {
        if (!cancelled) setQr(result?.enabled ? { status: "ready", links: result.links || {} } : { status: "disabled", links: {} });
      })
      .catch(() => {
        if (!cancelled) setQr({ status: "failed", links: {} });
      });
    return () => {
      cancelled = true;
    };
  }, [template.usesQr, qrVariantKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setOffsets(readDeviceCalibration(preset?.id));
    setStartPosition((current) => clampStartPosition(current, slotsPerPage));
  }, [preset?.id, slotsPerPage]);

  const labelJob = useMemo(() => buildPrintCenterLabelJob(mode.lines, preset, templateId, qr.links), [mode.lines, preset, templateId, qr.links]);
  const start = clampStartPosition(startPosition, slotsPerPage);
  const plan = useMemo(
    () => planSheetLayout({ labelCount: labelJob.labels.length, columns: geometry.columns, rows: geometry.rows, startPosition: start }),
    [geometry.columns, geometry.rows, labelJob.labels.length, start]
  );
  const layoutIssues = useMemo(() => [...validateSheetGeometry(geometry, offsets), ...template.validateFit(geometry, appearance)], [appearance, geometry, offsets, template]);
  const suggestion = useMemo(() => readSheetSuggestion(signature), [signature]);
  const geometryChangedSinceLastSheet = useMemo(() => lastSheetUsedOtherGeometry(signature), [signature]);

  const previewInput = useDeferredValue(
    useMemo(() => ({ labels: labelJob.labels, geometry, appearance, offsets, start, templateId }), [appearance, geometry, labelJob.labels, offsets, start, templateId])
  );
  const previewHtml = useMemo(() => {
    if (!previewInput.labels.length) return "";
    return buildLabelSheetDocument({
      labels: previewInput.labels,
      geometry: previewInput.geometry,
      calibration: previewInput.offsets,
      appearance: previewInput.appearance,
      templateId: previewInput.templateId,
      startPosition: previewInput.start,
      mode: LABEL_SHEET_MODES.PREVIEW,
      previewPageLimit: PREVIEW_PAGE_LIMIT,
      pageCaption: (page) =>
        `${fill(tt("pageCaption"), { page: page.pageNumber })}<span>${fill(tt("pageCaptionRange"), {
          from: page.firstPosition,
          to: page.lastPosition,
        })}</span>`,
    }).html;
  }, [previewInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const totals = useMemo(() => {
    const products = new Set(mode.lines.map((line) => line.item.productId)).size;
    const newStock = mode.lines.reduce((sum, line) => sum + Number(line.newStock || 0), 0);
    return { products, lines: mode.lines.length, labels: labelJob.totalLabels, newStock, extra: labelJob.totalLabels - newStock };
  }, [labelJob.totalLabels, mode.lines]);

  const setOffset = (key, delta) => {
    setOffsets((current) => {
      const next = { ...current, [key]: Math.round((Number(current[key] || 0) + delta) * 10) / 10 };
      saveDeviceCalibration(preset?.id, next);
      return next;
    });
  };

  const printAlignmentTest = () => {
    openPrintDocument(buildAlignmentTestDocument({ geometry, calibration: offsets }));
  };

  const handlePrint = async () => {
    setError(null);
    setIsPrinting(true);
    try {
      let job = mode.job;
      if (mode.type === "new") {
        job = await createJob({
          idempotencyKey: idempotencyKeyRef.current,
          source: mode.source,
          presetId: preset.id,
          ...(mode.reprintOfJobId ? { reprintOfJobId: mode.reprintOfJobId } : {}),
          lines: mode.jobLines,
        }).unwrap();
      }

      // The attempt is recorded before the print dialog opens: history shows
      // every time printing was invoked, even if the printer then fails.
      const updated = await recordAttempt({
        id: job.id,
        body: {
          startPosition: start,
          slotsPerPage,
          pageCount: plan.pageCount,
          labelCount: labelJob.labels.length,
          ...(plan.lastPosition ? { lastPosition: plan.lastPosition } : {}),
          layout: buildAttemptLayout({ preset, offsets, templateId }),
          ...(mode.lineIds?.length ? { lineIds: mode.lineIds } : {}),
        },
      }).unwrap();

      const { html } = buildLabelSheetDocument({
        labels: labelJob.labels,
        geometry,
        calibration: offsets,
        appearance,
        templateId,
        startPosition: start,
        mode: LABEL_SHEET_MODES.PRINT,
      });
      openPrintDocument(html);
      rememberSheetEnd({ geometrySignature: signature, lastPosition: plan.lastPosition, nextFreePosition: plan.nextFreePosition, slotsPerPage });
      onPrinted({ job: updated || job, plan, mode });
    } catch (apiError) {
      setError(getApiErrorDetails(apiError));
    } finally {
      setIsPrinting(false);
    }
  };

  const blocking = labelJob.failures;
  const unsafeDensity = labelJob.densityWarnings.filter((warning) => warning.level === "unsafe");
  const checkDensity = labelJob.densityWarnings.filter((warning) => warning.level !== "unsafe");
  const canPrint =
    !isPrinting &&
    qr.status !== "loading" &&
    labelJob.labels.length > 0 &&
    !blocking.length &&
    preset &&
    !layoutIssues.length &&
    (!unsafeDensity.length || densityOverride);

  return (
    <div className="pse-overlay pc-review-overlay" role="presentation">
      <button type="button" className="pse-overlay__backdrop" aria-label={tt("close")} onClick={isPrinting ? undefined : onClose} />
      <div className="pc-review" role="dialog" aria-modal="true" aria-labelledby="pc-review-title" ref={dialogRef} tabIndex={-1}>
        <header className="pc-review__head">
          <div>
            <p className="pc-eyebrow-label">{mode.type === "attempt" ? fill(tt("reviewAgainEyebrow"), { job: mode.job.id }) : tt("reviewEyebrow")}</p>
            <h2 id="pc-review-title" className="pc-review__title">
              {plural(tt, "reviewTitle", totals.labels)}
            </h2>
          </div>
          <button type="button" className="pse-icon-btn" aria-label={tt("close")} onClick={onClose} disabled={isPrinting}>
            <Icon name="x" size={20} />
          </button>
        </header>

        <div className="pc-review__body">
          <div className="pc-review__side">
            <section className="pc-review__summary" aria-label={tt("reviewSummary")}>
              <div><span>{tt("sumProducts")}</span><strong>{totals.products}</strong></div>
              <div><span>{tt("sumLines")}</span><strong>{totals.lines}</strong></div>
              <div><span>{tt("sumLabels")}</span><strong>{totals.labels}</strong></div>
              <div><span>{tt("sumPages")}</span><strong>{plan.pageCount}</strong></div>
              <p className="pc-review__split pc-small">
                <span className="pc-demand-text">{fill(tt("traySplitNew"), { count: totals.newStock })}</span>
                <span className="pc-muted">{fill(tt("traySplitExtra"), { count: totals.extra })}</span>
              </p>
              {totals.newStock > 0 ? <p className="pc-muted pc-small">{tt("reviewNewStockNote")}</p> : null}
            </section>

            <section className="pc-review__template" role="radiogroup" aria-label={tt("templateLabel")}>
              <span className="pc-filter__label">{tt("templateLabel")}</span>
              <div className="pc-template-options">
                {LABEL_TEMPLATE_IDS.map((id) => (
                  <button
                    key={id}
                    type="button"
                    role="radio"
                    aria-checked={id === templateId}
                    className={`pc-template-option${id === templateId ? " is-selected" : ""}`}
                    onClick={() => {
                      setTemplateId(id);
                      saveTemplateChoice(id);
                    }}
                  >
                    <strong>{tt(`template_${id}`)}</strong>
                    <span>{tt(`template_${id}_hint`)}</span>
                  </button>
                ))}
              </div>
            </section>

            <section className="pc-review__preset">
              <label className="pc-filter" htmlFor="pc-review-preset">
                <span className="pc-filter__label">{tt("presetLabel")}</span>
                <Select id="pc-review-preset" value={preset?.id || ""} onChange={(value) => onChangePreset(Number(value))}>
                  {presets.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.name}
                    </option>
                  ))}
                </Select>
              </label>
              <p className="pc-muted pc-small">
                {fill(tt("presetMeta"), {
                  columns: geometry.columns,
                  rows: geometry.rows,
                  slots: slotsPerPage,
                  width: metrics.labelWidthMM.toFixed(1),
                  height: metrics.labelHeightMM.toFixed(1),
                })}
              </p>
              <div className="pc-offsets" role="group" aria-label={tt("offsetsTitle")}>
                <span className="pc-filter__label">{tt("offsetsTitle")}</span>
                {[
                  ["printOffsetXMM", "offsetX"],
                  ["printOffsetYMM", "offsetY"],
                ].map(([key, labelKey]) => (
                  <div key={key} className="pc-offset">
                    <span>{tt(labelKey)}</span>
                    <button type="button" className="pse-stepper__btn" aria-label={fill(tt("offsetDecrease"), { axis: tt(labelKey) })} onClick={() => setOffset(key, -OFFSET_STEP)}>
                      <Icon name="minus" size={14} />
                    </button>
                    <strong>{Number(offsets[key] || 0).toFixed(1)} mm</strong>
                    <button type="button" className="pse-stepper__btn" aria-label={fill(tt("offsetIncrease"), { axis: tt(labelKey) })} onClick={() => setOffset(key, OFFSET_STEP)}>
                      <Icon name="plus" size={14} />
                    </button>
                  </div>
                ))}
                <p className="pc-muted pc-small">{tt("offsetsNote")}</p>
              </div>
              <div className="pc-review__preset-actions">
                <Btn size="sm" icon="grid" disabled={layoutIssues.length > 0} onClick={printAlignmentTest}>
                  {tt("alignmentTest")}
                </Btn>
                <Btn size="sm" variant="ghost" icon="sliders" onClick={onOpenPresetSettings}>
                  {isPresetAdmin ? tt("presetSettings") : tt("presetDetails")}
                </Btn>
              </div>
            </section>

            {layoutIssues.length ? (
              <Notice tone="danger" title={tt("layoutInvalidTitle")}>
                {tt("layoutInvalidCopy")}<br />
                <strong>{layoutIssues.map((issue) => tt(`layoutIssue_${issue}`)).join(" · ")}</strong>
              </Notice>
            ) : null}

            <SheetStartControls
              tt={tt}
              geometry={geometry}
              plan={plan}
              startPosition={start}
              slotsPerPage={slotsPerPage}
              onChangeStart={(value) => setStartPosition(clampStartPosition(value, slotsPerPage))}
              suggestion={suggestion}
            />

            {geometryChangedSinceLastSheet ? <Notice tone="warning">{tt("geometryChangedWarning")}</Notice> : null}

            {template.usesQr && (qr.status === "failed" || qr.status === "disabled") ? (
              <Notice tone="warning" title={tt("qrUnavailableTitle")}>
                {tt(qr.status === "failed" ? "qrFailedCopy" : "qrDisabledCopy")}
              </Notice>
            ) : null}

            {blocking.length ? (
              <Notice tone="danger" title={tt("renderBlockedTitle")}>
                <ul className="pc-issue-list">
                  {blocking.map((failure) => (
                    <li key={failure.key}>
                      <strong>{failure.item.productTitle}</strong> · {describeVariant(failure.item)} —{" "}
                      {fill(tt("renderBlockedReason"), { barcode: failure.item.barcode || "—" })}
                    </li>
                  ))}
                </ul>
              </Notice>
            ) : null}

            {unsafeDensity.length ? (
              <Notice tone="danger" title={tt("densityUnsafeTitle")}>
                <ul className="pc-issue-list">
                  {unsafeDensity.map((warning) => (
                    <li key={warning.key}>
                      <strong>{warning.item.productTitle}</strong> · {describeVariant(warning.item)} —{" "}
                      {fill(tt("densityReason"), { width: warning.moduleWidthMM.toFixed(2), barcode: warning.item.barcode })}
                    </li>
                  ))}
                </ul>
                <p className="pc-small">{tt("densityUnsafeCopy")}</p>
                <label className="pc-check-row">
                  <input
                    type="checkbox"
                    className="pc-check"
                    checked={densityOverride}
                    onChange={(event) => setDensityOverride(event.target.checked)}
                  />
                  <span>{tt("densityOverride")}</span>
                </label>
              </Notice>
            ) : null}

            {checkDensity.length ? (
              <Notice tone="warning" title={tt("densityTitle")}>
                <ul className="pc-issue-list">
                  {checkDensity.map((warning) => (
                    <li key={warning.key}>
                      <strong>{warning.item.productTitle}</strong> · {describeVariant(warning.item)} —{" "}
                      {fill(tt("densityReason"), { width: warning.moduleWidthMM.toFixed(2), barcode: warning.item.barcode })}
                    </li>
                  ))}
                </ul>
              </Notice>
            ) : null}

            {totals.labels > LARGE_JOB_LABELS ? <Notice tone="warning">{plural(tt, "largeJobWarning", totals.labels)}</Notice> : null}

            {error ? (
              <div ref={errorRef} role="alert">
                <ReviewError
                  tt={tt}
                  error={error}
                  onResolveConflict={onResolveConflict}
                  onRemoveKeys={onRemoveKeys}
                  onOpenJob={onOpenJob}
                  onClose={onClose}
                />
              </div>
            ) : null}
          </div>

          <div className="pc-review__preview">
            <div className="pc-review__preview-head">
              <p className="pc-muted pc-small pc-review__hint">
                <Icon name="info" size={14} /> {tt("previewHint")}
              </p>
              <Pill tone={DENSITY_TONES[labelJob.density]} title={tt("densityLegend")}>
                {tt(`density_${labelJob.density}`)}
              </Pill>
            </div>
            {previewHtml ? (
              <SheetPreview html={previewHtml} title={tt("previewFrameTitle")} paper={metrics.paper} tt={tt} onSelectPosition={(value) => setStartPosition(clampStartPosition(value, slotsPerPage))} />
            ) : null}
            {plan.pageCount > PREVIEW_PAGE_LIMIT ? (
              <p className="pc-muted pc-small">{plural(tt, "previewMorePages", plan.pageCount - PREVIEW_PAGE_LIMIT)}</p>
            ) : null}
          </div>
        </div>

        <footer className="pc-review__foot">
          <p className="pc-review__printer-hint pc-small">
            <Icon name="printer" size={15} /> {tt("printerChecklist")}
          </p>
          <div className="pc-review__actions">
            <Btn onClick={onClose} disabled={isPrinting}>{tt("back")}</Btn>
            <Btn variant="primary" icon="printer" disabled={!canPrint} onClick={handlePrint}>
              {isPrinting ? tt("printing") : plural(tt, "printLabels", totals.labels)}
            </Btn>
          </div>
        </footer>
      </div>
    </div>
  );
}

/** Server refusals, each with the way out. */
function ReviewError({ tt, error, onResolveConflict, onRemoveKeys, onOpenJob, onClose }) {
  if (error.code === "PRINT_DEMAND_CONFLICT") {
    const conflicts = error.details?.conflicts || [];
    return (
      <Notice tone="danger" title={tt("conflictTitle")}>
        <ul className="pc-issue-list">
          {conflicts.map((conflict) => (
            <li key={conflict.key}>
              <strong>{conflict.productTitle}</strong> · {conflict.variantTitle} —{" "}
              {fill(tt("conflictLine"), { requested: conflict.requested, available: conflict.available })}
              {(conflict.pendingJobs || []).map((job) => (
                <span key={job.jobId} className="pc-muted pc-small pc-block">
                  {fill(tt("conflictHeldBy"), { count: job.quantity, name: job.createdByName || "—", job: job.jobId })}
                  {onOpenJob ? (
                    <>
                      {" "}
                      <button
                        type="button"
                        className="pc-link"
                        onClick={() => {
                          onClose();
                          onOpenJob(job.jobId);
                        }}
                      >
                        {fill(tt("openJobNumber"), { job: job.jobId })}
                      </button>
                    </>
                  ) : null}
                </span>
              ))}
            </li>
          ))}
        </ul>
        <div className="pc-inline-actions">
          <Btn size="sm" onClick={() => { onResolveConflict(conflicts, "use_available"); onClose(); }}>
            {tt("conflictUseAvailable")}
          </Btn>
          <Btn size="sm" onClick={() => { onResolveConflict(conflicts, "as_extras"); onClose(); }}>
            {tt("conflictAsExtras")}
          </Btn>
        </div>
      </Notice>
    );
  }
  if (error.code === "PRINT_ITEMS_BLOCKED" || error.code === "PRINT_ITEMS_UNAVAILABLE") {
    const keys = error.code === "PRINT_ITEMS_BLOCKED" ? (error.details?.items || []).map((item) => item.key) : error.details?.keys || [];
    return (
      <Notice tone="danger" title={tt(error.code === "PRINT_ITEMS_BLOCKED" ? "blockedServerTitle" : "unavailableServerTitle")}>
        <ul className="pc-issue-list">
          {(error.details?.items || keys.map((key) => ({ key, productTitle: key }))).map((item) => (
            <li key={item.key}>
              <strong>{item.productTitle}</strong>
              {item.variantTitle ? ` · ${item.variantTitle}` : ""}
            </li>
          ))}
        </ul>
        <Btn size="sm" onClick={() => { onRemoveKeys(keys); onClose(); }}>{tt("removeTheseItems")}</Btn>
      </Notice>
    );
  }
  if (error.code === "PRINT_JOB_CLOSED") {
    return <Notice tone="danger">{tt("jobClosedError")}</Notice>;
  }
  return <Notice tone="danger">{error.message || tt("actionError")}</Notice>;
}

export default PrintReviewDialog;
